import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import type { ComponentProps } from "react";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { ChatPopContainer } from "~/components/common/chat/ChatPopContainer";
import { DealConstructionScheduleVendorCard } from "~/components/common/deals/show/construction_card/DealConstructionScheduleVendorCard";
import { DealCard } from "~/components/common/deals/show/deal_card/DealCard";
import { DealCompanyCard } from "~/components/common/deals/show/deal_card/DealCompanyCard";
import { DealWitnessCard } from "~/components/common/deals/show/deal_card/DealWitnessCard";
import { DealInspectionCandidateDateCard } from "~/components/common/deals/show/inspection_candidate_date_card/DealInspectionCandidateDateCard";
import { DealMain } from "~/components/common/deals/show/main/DealMain";
import { DealRepairCard } from "~/components/common/deals/show/repair_card/DealRepairCard";
import { DealNoteWithinVendorCard } from "~/components/common/deals/show/vendor_card/DealNoteWithinVendorCard";
import { DealOriginVendorCard } from "~/components/common/deals/show/vendor_card/DealOriginVendorCard";
import { DealSubVendorCard } from "~/components/common/deals/show/vendor_card/DealSubVendorCard";
import { DealVendorNoteCard } from "~/components/common/deals/show/vendor_card/DealVendorNoteCard";
import { DealVisitDateVendorCard } from "~/components/common/deals/show/visit_date_card/DealVisitDateVendorCard";
import { VendorDealLayout } from "~/components/common/layouts/deals/VendorDealLayout";
import { VendorLayoutV2 } from "~/components/common/layouts/VendorLayoutV2";
import Loader from "~/components/common/parts/loading/Loading";
import { SERVER_ERROR_MESSAGE } from "~/constants/AppConst";
import { VendorSessionProvider } from "~/context/VendorSession";
import { useMessages } from "~/hooks/vendors/deals/useMessages";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import {
	getVendorMessageReceiver,
	UPDATE_TEMPORARY_UNREAD_MESSAGE
} from "~/models/Message";
import { PATH as DEALS_PATH } from "~/pages/vendors/deals";
import { isSuccess } from "~/utils/api/api_handler";
import { postTemporaryUnread } from "~/utils/api/vendors/deals/Message";
import Styles from "./Page.module.scss";

export function Page() {
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const [isOpen, setIsOpen] = useState(false);
	const { data: deal, isLoading, mutate: mutateDeal } = useDeals().show(id);
	const { data: session } = useSessions().index();
	const { data: messages, mutate: mutateMessages } = useMessages().index(id);
	const handleToggleChat = useCallback(() => {
		if (isOpen) {
			setIsOpen(false);
		} else {
			setIsOpen(true);
		}
	}, [isOpen]);
	const handleMutateDeal = useCallback(async () => {
		await mutateDeal();
	}, [mutateDeal]);
	const handleMutateMessage = useCallback(async () => {
		await mutateMessages();
		await mutateDeal();
	}, [mutateDeal, mutateMessages]);
	if (isLoading || deal === null || session === null) {
		return <Loader isLoading />;
	}
	const handleTemporaryUnread = async (): Promise<void> => {
		const result = await postTemporaryUnread(deal.id);
		if (isSuccess(result)) {
			toast.success(UPDATE_TEMPORARY_UNREAD_MESSAGE);
			await mutateDeal();
			setIsOpen(false);
		} else {
			toast.error(SERVER_ERROR_MESSAGE);
		}
	};
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ label: "案件詳細" }
	] as const satisfies ComponentProps<typeof VendorDealLayout>["items"];
	return (
		<VendorSessionProvider session={session}>
			<VendorLayoutV2>
				<VendorDealLayout deal={deal} items={items} noBackground>
					<div className={Styles.Container}>
						<div className={Styles.LeftCol}>
							<DealCompanyCard
								deal={deal}
								handleToggleChat={handleToggleChat}
							/>
							<DealVendorNoteCard
								deal={deal}
								handleMutateDeal={handleMutateDeal}
							/>
							<DealNoteWithinVendorCard
								deal={deal}
								handleMutate={handleMutateDeal}
							/>
							<DealInspectionCandidateDateCard
								deal={deal}
								handleMutate={handleMutateDeal}
								isVendor
							/>
							<DealWitnessCard deal={deal} />
							{deal.dealType === "repair" && (
								<DealRepairCard companyType="vendor" deal={deal} />
							)}
							<DealCard deal={deal} isVendor vendorSession={session} />
							<DealOriginVendorCard deal={deal} />
							<DealSubVendorCard deal={deal} />
							<DealVisitDateVendorCard />
							<DealConstructionScheduleVendorCard deal={deal} />
						</div>
						<DealMain deal={deal} isAdmin={session.admin} isVendor />
					</div>
				</VendorDealLayout>
				{messages !== null && (
					<ChatPopContainer
						deal={deal}
						handleClose={() => {
							setIsOpen(false);
						}}
						handleMutate={handleMutateMessage}
						handleTemporaryUnread={handleTemporaryUnread}
						isOpen={isOpen}
						isVendor
						messages={messages}
						receiver={getVendorMessageReceiver(deal, session)}
					/>
				)}
			</VendorLayoutV2>
		</VendorSessionProvider>
	);
}
